import { Link } from 'react-router-dom'
import { SITE } from '../config/site'
import { LegalLayout } from './LegalLayout'
import styles from './LegalPage.module.css'

export function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Use"
      lede={`These terms govern your use of ${SITE.productName}, its website, trial, and personal software license.`}
    >
      <section>
        <h2>1. Acceptance</h2>
        <p>
          These Terms of Use are an agreement between you and {SITE.legalName}. By
          downloading, installing, purchasing, or using Gesturely, you agree to these
          terms. If you do not agree, do not use Gesturely.
        </p>
      </section>

      <section>
        <h2>2. Eligibility and lawful use</h2>
        <p>
          You must be legally able to enter into this agreement. You may use Gesturely
          only in compliance with applicable law and must not use it to infringe the
          rights of others or interfere with systems, services, or users.
        </p>
      </section>

      <section>
        <h2>3. Trial and personal license</h2>
        <p>
          Gesturely includes a 14-day full-feature trial inside the app so you can
          evaluate it before purchase. The trial is not a Paddle subscription or a
          recurring billing trial.
        </p>
        <p>
          A completed purchase grants you a limited, personal, non-exclusive,
          non-transferable, revocable license to install and use Gesturely on up to two
          personal Macs that you use or control. The license is for the software as
          made available with your purchase; it does not promise future features,
          indefinite support, lifetime updates, free future major versions, or
          compatibility with future macOS releases.
        </p>
      </section>

      <section>
        <h2>4. Purchases and payment</h2>
        <p>
          Gesturely is currently offered for $14.99 USD as a one-time purchase, with no
          subscription. Payments are processed by Paddle, our Merchant of Record. Your
          transaction is also subject to the buyer terms and payment procedures Paddle
          presents at checkout. Paddle processes payment-card details; {SITE.legalName}{' '}
          does not directly collect or store your full card details.
        </p>
      </section>

      <section>
        <h2>5. Refunds</h2>
        <p>
          Because Gesturely can be fully evaluated during its 14-day trial before
          purchase, purchases are generally final and non-refundable. Limited
          exceptions and legally required rights are described in our{' '}
          <Link to="/refunds">Refund Policy</Link>, which forms part of these terms.
        </p>
      </section>

      <section>
        <h2>6. Restrictions</h2>
        <p>You may not, except where applicable law expressly permits you to:</p>
        <ul>
          <li>redistribute, sublicense, rent, lease, sell, or resell Gesturely or a license;</li>
          <li>share a license outside the permitted personal use on up to two Macs;</li>
          <li>circumvent licensing, access, security, or technical restrictions;</li>
          <li>reverse engineer, decompile, or attempt to derive source code; or</li>
          <li>use Gesturely or its website in a fraudulent, abusive, or unlawful manner.</li>
        </ul>
      </section>

      <section>
        <h2>7. Ownership</h2>
        <p>
          Gesturely, its website, design, software, documentation, trademarks, and
          related intellectual property are owned by {SITE.legalName} or its licensors.
          Your purchase provides only the limited license stated above and does not
          transfer ownership of Gesturely or its intellectual property.
        </p>
      </section>

      <section>
        <h2>8. Updates and product changes</h2>
        <p>
          We may provide updates, modify features, discontinue features, or change
          technical requirements. Updates may be needed for security, reliability, or
          compatibility. We do not guarantee that any particular feature, update,
          service, or support channel will remain available indefinitely.
        </p>
      </section>

      <section>
        <h2>9. Availability and compatibility</h2>
        <p>
          Gesturely currently requires macOS 14 or later. Hardware, operating-system,
          application, accessibility-permission, and configuration differences may
          affect functionality. We do not guarantee compatibility with every Mac,
          third-party application, accessory, or future macOS release. Network-based
          purchase, licensing, email, website, and update services may occasionally be
          unavailable.
        </p>
      </section>

      <section>
        <h2>10. Warranty disclaimer</h2>
        <p>
          To the fullest extent permitted by law, Gesturely and its website are
          provided “as is” and “as available,” without warranties of any kind, whether
          express, implied, or statutory, including implied warranties of
          merchantability, fitness for a particular purpose, title, and
          non-infringement. Nothing in these terms excludes a warranty or consumer
          right that cannot lawfully be excluded.
        </p>
      </section>

      <section>
        <h2>11. Limitation of liability</h2>
        <p>
          To the fullest extent permitted by law, {SITE.legalName} will not be liable
          for indirect, incidental, special, consequential, exemplary, or punitive
          damages, or for lost profits, revenues, data, use, or goodwill arising from
          Gesturely or these terms. To the fullest extent permitted by law, our total
          liability for claims relating to Gesturely will not exceed the amount you
          paid for Gesturely. These limits do not apply where liability cannot lawfully
          be limited or excluded.
        </p>
      </section>

      <section>
        <h2>12. Suspension and termination</h2>
        <p>
          We may suspend or revoke a license if the purchase is refunded, reversed,
          fraudulent, subject to a chargeback, or used in material violation of these
          terms. On termination, you must stop using Gesturely and remove copies covered
          by the terminated license. Provisions that by their nature should survive
          termination will remain in effect.
        </p>
      </section>

      <section>
        <h2>13. Governing law</h2>
        <p>
          These terms are governed by the laws of the State of Wyoming, United States,
          without regard to conflict-of-law principles. This choice does not deprive
          you of mandatory consumer protections or rights that apply under the law of
          your place of residence and cannot be waived by agreement.
        </p>
      </section>

      <section>
        <h2>14. Changes and general terms</h2>
        <p>
          We may update these terms as Gesturely or applicable law changes. Updated
          terms will be posted with a new effective date. If any provision is held
          unenforceable, the remaining provisions remain in effect. Our failure to
          enforce a provision is not a waiver. These terms, together with the policies
          they reference and applicable Paddle buyer terms, are the agreement governing
          your use of Gesturely.
        </p>
      </section>

      <section>
        <h2>15. Contact</h2>
        <p>
          Questions about these terms can be sent to{' '}
          <a href={`mailto:${SITE.supportEmail}`}>{SITE.supportEmail}</a>.
        </p>
        <address className={styles.address}>
          {SITE.legalName}
          <br />
          {SITE.legalAddress[0]}
          <br />
          {SITE.legalAddress[1]}
          <br />
          {SITE.legalAddress[2]}
        </address>
      </section>
    </LegalLayout>
  )
}
